/**
 * @file FontDeleteDialog.tsx
 * @description Удаление своего шрифта магазина (phase19.16 Р1-7; SB99 —
 * шрифт из опубликованного вида сайта или сохранённого дизайна удалить
 * нельзя; проект uiux §5).
 *
 * Можно — «Удалить шрифт «…»?» с окончательной красной кнопкой; если шрифт
 * выбран только в черновике — строка, что там будет шрифт по умолчанию.
 * Нельзя (признак сервера `canDelete = false` или ответ 409 font_in_use) —
 * «Шрифт «…» используется»: где именно и что сделать, [Открыть «Дизайны»]
 * [Понятно]. После удаления черновик перечитывает контекст шрифтов (сервер
 * переводит роли черновика на шрифт по умолчанию).
 *
 * Роли черновика для предупреждения считаются по ТЕКУЩЕЙ теме кабинета
 * (автосохранение может отставать от `usedIn.draftRoles` сервера); серверные
 * — только если темы нет. Ответ 409 хранится вместе с id шрифта: для другого
 * шрифта он просто не подходит, сбрасывать его в эффекте не нужно.
 */

import { shopFontKey, type SiteTheme } from "@chaiberi/shared";
import { useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { useShopFonts } from "../shop-fonts-context";
import { fontErrorText, parseFontInUse } from "../../../lib/site-view/shop-font-errors";
import { draftRolesText, fontUnblockText, fontUsageLines, localDraftRoles, OLDER_REVISIONS_TEXT } from "../../../lib/site-view/shop-font-usage";
import type { ShopFontDto, ShopFontUsage } from "../../../lib/site-view/shop-fonts";

interface Props {
  /** Шрифт к удалению; `null` — окно закрыто. */
  font: ShopFontDto | null;
  /** Шрифты ролей текущей темы кабинета; `null` — темы нет, роли берём у сервера. */
  localFonts?: SiteTheme["fonts"] | null;
  onClose: () => void;
  onDeleted: (font: ShopFontDto) => void;
  /** Перейти к дизайнам (кнопка видна, если шрифт в дизайне и переход есть). */
  onOpenDesigns?: () => void;
}

export function FontDeleteDialog({ font, localFonts = null, onClose, onDeleted, onOpenDesigns }: Props) {
  const shop = useShopFonts();
  const notify = useNotify();
  const [inUse, setInUse] = useState<{ fontId: string; usage: ShopFontUsage } | null>(null);
  const [pending, setPending] = useState(false);

  const serverUsage = inUse !== null && inUse.fontId === font?.id ? inUse.usage : null;
  const usage = serverUsage ?? font?.usedIn ?? null;
  // Готовый признак сервера `canDelete`; ответ 409 font_in_use — тоже «нельзя».
  const blocked = usage !== null && (serverUsage !== null || !font?.canDelete);
  const draftRoles = font ? (localFonts ? localDraftRoles(localFonts, shopFontKey(font.id)) : font.usedIn.draftRoles) : [];
  const draftLine = !blocked ? draftRolesText(draftRoles) : null;

  async function remove() {
    if (!font) return;
    setPending(true);
    try {
      const result = await shop.remove(font.id);
      notify.success(`Шрифт «${font.family}» удалён${result.olderRevisions ? `. ${OLDER_REVISIONS_TEXT}` : ""}`);
      onDeleted(font);
      onClose();
    } catch (err) {
      const used = parseFontInUse(err);
      if (used) setInUse({ fontId: font.id, usage: used });
      else notify.error(fontErrorText(err, "Не удалось удалить шрифт."));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={font !== null} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-md">
        {font && usage && blocked ? (
          <>
            <DialogHeader>
              <DialogTitle>Шрифт «{font.family}» используется</DialogTitle>
            </DialogHeader>
            <ul className="type-body flex list-disc flex-col gap-1 pl-5 text-on-surface">
              {fontUsageLines(usage).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <DialogDescription>{fontUnblockText(usage)}</DialogDescription>
            <DialogFooter>
              {onOpenDesigns && usage.designs.length > 0 ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    onClose();
                    onOpenDesigns();
                  }}
                >
                  Открыть «Дизайны»
                </Button>
              ) : null}
              <Button onClick={onClose}>Понятно</Button>
            </DialogFooter>
          </>
        ) : font ? (
          <>
            <DialogHeader>
              <DialogTitle>Удалить шрифт «{font.family}»?</DialogTitle>
              <DialogDescription>Файлы шрифта удалятся из магазина. Вернуть их можно только новой загрузкой.</DialogDescription>
            </DialogHeader>
            {draftLine ? <p className="type-body text-on-surface">{draftLine}</p> : null}
            <DialogFooter>
              <Button variant="ghost" disabled={pending} onClick={onClose}>
                Отмена
              </Button>
              <Button variant="destructive-solid" loading={pending} onClick={() => void remove()}>
                Удалить
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
